import { mcGetItem, mcSetItem, mcSetCloudItem } from "./mcCloudSync";

export const MC_ACCOUNTABILITY_HISTORY_KEY = "mc-accountability-history";
export const MC_ACCOUNTABILITY_EVENT = "mc-accountability-change";

const MAX_HISTORY_ROWS = 6000;

function cleanText(value = "") {
  return String(value ?? "").trim();
}

function numberValue(value) {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(String(value).replace(/,/g, "."));
  return Number.isFinite(parsed) ? parsed : 0;
}

function round2(value) {
  return Math.round(numberValue(value) * 100) / 100;
}

function slug(value = "") {
  return cleanText(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "unassigned";
}

function lineTouched(line = {}) {
  const hasCounted = line.counted !== "" && line.counted !== undefined && line.counted !== null;
  const hasWaste = line.waste !== "" && line.waste !== undefined && line.waste !== null;
  const hasNote = Boolean(cleanText(line.note || line.notes));
  return hasCounted || hasWaste || hasNote;
}

function lineVariance(line = {}) {
  if (line.variance !== undefined && line.variance !== null && line.variance !== "") return round2(line.variance);
  const counted = numberValue(line.counted);
  const waste = numberValue(line.waste);
  const systemSoh = numberValue(line.systemSoh ?? line.systemSOH ?? line.soh ?? line.realSoh ?? 0);
  return round2(counted + waste - systemSoh);
}

function lineExposure(line = {}, variance = 0) {
  const direct = line.costVariance ?? line.finalCostVariance ?? line.varianceValue ?? line.exposure;
  if (direct !== undefined && direct !== null && direct !== "") return round2(direct);
  const unitCost = numberValue(line.cost ?? line.effectiveCost ?? line.retail_price ?? line.price ?? 0);
  return round2(numberValue(variance) * unitCost);
}

function resolveProductKey(line = {}) {
  const sku = cleanText(line.sku || line.SKU || line.id || line.productId);
  const product = cleanText(line.product || line.name || line.productName || line.item || line.description);
  const category = cleanText(line.category || line.normalizedCategory || line.group || line.product_category);
  return sku || `${slug(product)}:${slug(category)}`;
}

export function loadAccountabilityHistory() {
  try {
    const parsed = JSON.parse(mcGetItem(MC_ACCOUNTABILITY_HISTORY_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (_) {
    return [];
  }
}

export function saveAccountabilityHistory(rows = []) {
  const cleanRows = Array.isArray(rows) ? rows.filter(Boolean).slice(0, MAX_HISTORY_ROWS) : [];
  const value = JSON.stringify(cleanRows);
  try { mcSetItem(MC_ACCOUNTABILITY_HISTORY_KEY, value); } catch (_) {}
  try { mcSetCloudItem(MC_ACCOUNTABILITY_HISTORY_KEY, value).catch(() => {}); } catch (_) {}
  try {
    window.dispatchEvent(new CustomEvent(MC_ACCOUNTABILITY_EVENT, { detail: { rows: cleanRows } }));
  } catch (_) {}
  return cleanRows;
}

export function buildVarianceSnapshotsFromSession(sessionRecord = {}, detailSnapshot = {}) {
  const lines = Array.isArray(detailSnapshot?.lines) ? detailSnapshot.lines : [];
  if (!lines.length || !sessionRecord?.sessionId) return [];

  const meta = detailSnapshot?.meta && typeof detailSnapshot.meta === "object" ? detailSnapshot.meta : {};
  const sessionId = cleanText(sessionRecord.sessionId || meta.sessionId);
  const countedAt = cleanText(sessionRecord.archivedAt || sessionRecord.updatedAt || detailSnapshot.savedAt || meta.updatedAt || meta.createdAt || new Date().toISOString());
  const siteName = cleanText(sessionRecord.storeName || sessionRecord.store || meta.storeName || meta.store || meta.siteName || "Unknown site");
  const sectionId = cleanText(sessionRecord.sectionId || meta.sectionId || (sessionRecord.fullSite || meta.fullSite ? "full-count" : "unassigned"));
  const sectionName = cleanText(sessionRecord.sectionName || meta.sectionName || (sessionRecord.fullSite || meta.fullSite ? "Full Count" : "Unassigned"));
  const cycleId = cleanText(sessionRecord.cycleId || meta.cycleId || meta.cycleKey || sessionRecord.cycle?.cycleId || sessionRecord.cycle?.id);
  const cycleName = cleanText(sessionRecord.cycleName || meta.cycleName || sessionRecord.cycle?.cycleName || sessionRecord.cycle?.name || "Unassigned Cycle");
  const countedBy = cleanText(sessionRecord.lastEditedBy || sessionRecord.startedBy || meta.lastEditedBy || meta.startedBy || meta.createdBy || "Unknown user");

  return lines
    .filter(lineTouched)
    .map((line = {}) => {
      const variance = lineVariance(line);
      const exposure = lineExposure(line, variance);
      const product = cleanText(line.product || line.name || line.productName || line.item || line.description || "Unnamed product");
      const sku = cleanText(line.sku || line.SKU || line.id || line.productId);
      const category = cleanText(line.category || line.normalizedCategory || line.group || line.product_category || "Unassigned category");
      const productKey = resolveProductKey(line);
      const systemSoh = round2(line.systemSoh ?? line.systemSOH ?? line.soh ?? line.realSoh ?? 0);
      const counted = round2(line.counted ?? line.realSoh ?? 0);
      const waste = round2(line.waste ?? 0);
      const unitCost = round2(line.cost ?? line.effectiveCost ?? line.retail_price ?? line.price ?? 0);
      return {
        id: `${sessionId}:${productKey}`,
        sessionId,
        siteName,
        siteKey: slug(siteName),
        sectionId: sectionId || "unassigned",
        sectionName: sectionName || "Unassigned",
        cycleId,
        cycleName,
        countedBy,
        countedAt,
        productKey,
        product,
        sku,
        category,
        systemSoh,
        counted,
        waste,
        unitCost,
        variance,
        exposure,
        absExposure: Math.abs(exposure),
        note: cleanText(line.note || line.notes),
        direction: exposure < 0 ? "loss" : exposure > 0 ? "gain" : "neutral",
      };
    })
    .filter((row) => row.note || row.variance !== 0 || row.exposure !== 0);
}

export function appendVarianceHistoryForArchivedSession(sessionRecord = {}, detailSnapshot = {}) {
  const additions = buildVarianceSnapshotsFromSession(sessionRecord, detailSnapshot);
  if (!additions.length) return loadAccountabilityHistory();
  const existing = loadAccountabilityHistory();
  const merged = new Map();
  [...additions, ...existing].forEach((row) => {
    if (!row?.id) return;
    merged.set(row.id, row);
  });
  const next = Array.from(merged.values()).sort((a, b) => new Date(b.countedAt || 0).getTime() - new Date(a.countedAt || 0).getTime());
  return saveAccountabilityHistory(next);
}

export function buildAccountabilitySummary(rows = []) {
  const safeRows = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const productMap = new Map();
  const sectionMap = new Map();

  safeRows.forEach((row) => {
    const productKey = `${row.siteKey || slug(row.siteName)}|${row.sectionId || slug(row.sectionName)}|${row.productKey || slug(row.product)}`;
    const product = productMap.get(productKey) || {
      key: productKey,
      product: row.product || "Unnamed product",
      category: row.category || "Unassigned category",
      sectionName: row.sectionName || "Unassigned",
      siteName: row.siteName || "Unknown site",
      occurrences: 0,
      exposure: 0,
      absExposure: 0,
      sessions: new Set(),
      latestAt: row.countedAt || "",
    };
    product.occurrences += 1;
    product.exposure = round2(product.exposure + numberValue(row.exposure));
    product.absExposure = round2(product.absExposure + Math.abs(numberValue(row.exposure)));
    product.sessions.add(row.sessionId);
    if (new Date(row.countedAt || 0).getTime() > new Date(product.latestAt || 0).getTime()) product.latestAt = row.countedAt;
    productMap.set(productKey, product);

    const sectionKey = `${row.siteKey || slug(row.siteName)}|${row.sectionId || slug(row.sectionName)}`;
    const section = sectionMap.get(sectionKey) || {
      key: sectionKey,
      sectionName: row.sectionName || "Unassigned",
      siteName: row.siteName || "Unknown site",
      flags: 0,
      exposure: 0,
      absExposure: 0,
      sessions: new Set(),
      bySession: new Map(),
    };
    section.flags += 1;
    section.exposure = round2(section.exposure + numberValue(row.exposure));
    section.absExposure = round2(section.absExposure + Math.abs(numberValue(row.exposure)));
    section.sessions.add(row.sessionId);
    const currentSession = section.bySession.get(row.sessionId) || { sessionId: row.sessionId, countedAt: row.countedAt, exposure: 0 };
    currentSession.exposure = round2(currentSession.exposure + numberValue(row.exposure));
    if (new Date(row.countedAt || 0).getTime() > new Date(currentSession.countedAt || 0).getTime()) currentSession.countedAt = row.countedAt;
    section.bySession.set(row.sessionId, currentSession);
    sectionMap.set(sectionKey, section);
  });

  const recurringProducts = Array.from(productMap.values())
    .map((item) => ({ ...item, sessions: item.sessions.size }))
    .filter((item) => item.occurrences >= 2 || item.sessions >= 2)
    .sort((a, b) => b.occurrences - a.occurrences || b.absExposure - a.absExposure)
    .slice(0, 5);

  const highestRiskSections = Array.from(sectionMap.values())
    .map((item) => ({ ...item, sessions: item.sessions.size, bySession: Array.from(item.bySession.values()) }))
    .sort((a, b) => b.absExposure - a.absExposure || b.flags - a.flags)
    .slice(0, 5);

  const trendingWorse = highestRiskSections
    .map((section) => {
      const sessions = [...section.bySession].sort((a, b) => new Date(a.countedAt || 0).getTime() - new Date(b.countedAt || 0).getTime());
      if (sessions.length < 2) return null;
      const previous = sessions[sessions.length - 2];
      const latest = sessions[sessions.length - 1];
      const movement = round2(numberValue(latest.exposure) - numberValue(previous.exposure));
      return { ...section, previousExposure: previous.exposure, latestExposure: latest.exposure, movement };
    })
    .filter(Boolean)
    .filter((item) => item.movement < 0)
    .sort((a, b) => Math.abs(b.movement) - Math.abs(a.movement))
    .slice(0, 5);

  return {
    rows: safeRows,
    recurringProducts,
    highestRiskSections,
    trendingWorse,
    totalFlags: safeRows.length,
    totalExposure: round2(safeRows.reduce((sum, row) => sum + numberValue(row.exposure), 0)),
  };
}
